import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [recurringPaymentPrompt, setRecurringPaymentPrompt] = useState(null);

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/invoices`, { withCredentials: true });
      setInvoices(response.data);
      setFilteredInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Failed to load invoices');
    }
  }, []);

  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setFilteredInvoices(invoices);
      return;
    }
    try {
      const response = await axios.get(`${API}/invoices/search?q=${encodeURIComponent(query)}`, { withCredentials: true });
      setFilteredInvoices(response.data);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    }
  }, [invoices]);

  const handleFilterChange = useCallback(async (filters) => {
    const params = new URLSearchParams();
    if (filters.vendor) params.append('vendor', filters.vendor);
    if (filters.category && filters.category !== 'All') params.append('category', filters.category);
    if (filters.status) params.append('status', filters.status);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.minAmount) params.append('min_amount', filters.minAmount);
    if (filters.maxAmount) params.append('max_amount', filters.maxAmount);

    if (params.toString() === '') {
      setFilteredInvoices(invoices);
      return;
    }

    try {
      const response = await axios.get(`${API}/invoices/search?${params.toString()}`, { withCredentials: true });
      setFilteredInvoices(response.data);
    } catch (error) {
      console.error('Filter failed:', error);
    }
  }, [invoices]);

  const handleFileSelect = useCallback(async (file, paymentStatus = 'unpaid') => {
    setIsProcessing(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/invoices/upload?payment_status=${paymentStatus}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newInvoice = response.data;
      const extractedData = newInvoice.extracted_data;
      
      // Check for duplicates with extracted data
      if (extractedData) {
        const duplicateCheckData = {
          invoice_number: extractedData.invoice_number,
          total_amount: extractedData.total_amount,
          vendor_name: extractedData.vendor_name,
          exclude_invoice_id: newInvoice.invoice_id
        };
        
        const hasEnoughData = [
          duplicateCheckData.invoice_number,
          duplicateCheckData.total_amount,
          duplicateCheckData.vendor_name
        ].filter(Boolean).length >= 2;

        if (hasEnoughData) {
          try {
            const dupCheckResponse = await axios.post(`${API}/invoices/check-duplicate`, duplicateCheckData, {
              withCredentials: true
            });

            if (dupCheckResponse.data.is_duplicate) {
              setDuplicateInfo({
                ...dupCheckResponse.data,
                newInvoiceId: newInvoice.invoice_id,
                newInvoice: newInvoice
              });
              setIsProcessing(false);
              await fetchInvoices();
              return { isDuplicate: true };
            }
          } catch (e) {
            console.error('Duplicate check error:', e);
          }
        }
      }

      toast.success('Invoice processed successfully!');
      setSelectedInvoice(newInvoice);
      setShowReviewModal(true);
      await fetchInvoices();
      
      // Check if this is a detected recurring payment
      const detectedType = newInvoice.extracted_data?.detected_payment_type;
      if (detectedType === 'rent' || detectedType === 'loan') {
        setTimeout(() => {
          setRecurringPaymentPrompt(newInvoice);
        }, 500);
      }

      return { success: true, invoice: newInvoice };
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process invoice');
      return { error: true };
    } finally {
      setIsProcessing(false);
    }
  }, [fetchInvoices]);

  const handleMarkPaid = useCallback(async (invoiceId, paidAmount = null, paymentNotes = '') => {
    try {
      const params = new URLSearchParams({ status: 'paid' });
      if (paidAmount) params.append('paid_amount', paidAmount);
      if (paymentNotes) params.append('payment_notes', paymentNotes);
      
      await axios.patch(`${API}/invoices/${invoiceId}/payment-status?${params.toString()}`, {}, {
        withCredentials: true
      });
      toast.success('Invoice marked as paid');
      await fetchInvoices();
    } catch (error) {
      console.error('Mark paid error:', error);
      toast.error('Failed to mark invoice as paid');
    }
  }, [fetchInvoices]);

  const handleMarkUnpaid = useCallback(async (invoiceId) => {
    try {
      await axios.patch(`${API}/invoices/${invoiceId}/payment-status?status=unpaid`, {}, {
        withCredentials: true
      });
      toast.success('Invoice marked as unpaid');
      await fetchInvoices();
    } catch (error) {
      console.error('Mark unpaid error:', error);
      toast.error('Failed to mark invoice as unpaid');
    }
  }, [fetchInvoices]);

  const handleDuplicateConfirm = useCallback(async () => {
    if (duplicateInfo?.newInvoice) {
      toast.success('Invoice uploaded successfully!');
      setSelectedInvoice(duplicateInfo.newInvoice);
      setShowReviewModal(true);
    }
    setDuplicateInfo(null);
  }, [duplicateInfo]);

  const handleDuplicateCancel = useCallback(async () => {
    if (duplicateInfo?.newInvoiceId) {
      try {
        await axios.delete(`${API}/invoices/${duplicateInfo.newInvoiceId}`, {
          withCredentials: true
        });
        toast.info('Duplicate invoice discarded');
        await fetchInvoices();
      } catch (error) {
        console.error('Error deleting duplicate:', error);
        toast.error('Failed to discard duplicate invoice');
      }
    }
    setDuplicateInfo(null);
  }, [duplicateInfo, fetchInvoices]);

  const handleSaveInvoice = useCallback(async (updatedData) => {
    try {
      await axios.put(
        `${API}/invoices/${selectedInvoice.invoice_id}`,
        { extracted_data: updatedData },
        { withCredentials: true }
      );

      toast.success('Invoice updated successfully!');
      setShowReviewModal(false);
      setSelectedInvoice(null);
      await fetchInvoices();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update invoice');
    }
  }, [selectedInvoice, fetchInvoices]);

  const handleEditInvoice = useCallback((invoice) => {
    setSelectedInvoice(invoice);
    setShowReviewModal(true);
  }, []);

  const handleDeleteInvoice = useCallback(async (invoice) => {
    if (!window.confirm(`Delete invoice "${invoice.filename}"? You can re-upload with a clearer image.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/invoices/${invoice.invoice_id}`, {
        withCredentials: true
      });
      toast.success('Invoice deleted. Please re-upload with a clearer image.');
      await fetchInvoices();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete invoice');
    }
  }, [fetchInvoices]);

  const handleDownloadInvoice = useCallback(async (invoice) => {
    try {
      const response = await axios.get(
        `${API}/invoices/${invoice.invoice_id}/download`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Excel downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download Excel');
    }
  }, []);

  const handleDownloadFormat = useCallback(async (invoice, format) => {
    try {
      const formatConfig = {
        xlsx: { endpoint: 'download', ext: 'xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', name: 'Excel' },
        pdf: { endpoint: 'download/pdf', ext: 'pdf', mime: 'application/pdf', name: 'PDF' },
        docx: { endpoint: 'download/docx', ext: 'docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', name: 'Word' },
        txt: { endpoint: 'download/txt', ext: 'txt', mime: 'text/plain', name: 'Text' }
      };

      const config = formatConfig[format] || formatConfig.xlsx;

      const response = await axios.get(
        `${API}/invoices/${invoice.invoice_id}/${config.endpoint}`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data], { type: config.mime }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_id}.${config.ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${config.name} downloaded successfully!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    }
  }, []);

  const closeReviewModal = useCallback(() => {
    setShowReviewModal(false);
    setSelectedInvoice(null);
  }, []);

  const closeRecurringPrompt = useCallback(() => {
    setRecurringPaymentPrompt(null);
  }, []);

  return {
    // State
    invoices,
    filteredInvoices,
    isProcessing,
    selectedInvoice,
    showReviewModal,
    duplicateInfo,
    recurringPaymentPrompt,
    // Handlers
    fetchInvoices,
    handleSearch,
    handleFilterChange,
    handleFileSelect,
    handleDuplicateConfirm,
    handleDuplicateCancel,
    handleSaveInvoice,
    handleEditInvoice,
    handleDeleteInvoice,
    handleDownloadInvoice,
    handleDownloadFormat,
    handleMarkPaid,
    handleMarkUnpaid,
    closeReviewModal,
    closeRecurringPrompt,
  };
}
